import React, { ChangeEvent, memo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useLanguage } from '~/Hooks';
import { selectActiveTrainerList } from '~/store/provider';
import { IModalProps } from './types';
import { ModalSelectPopover } from './ModalSelectPopover';
import { ModalSelectCheckboxList } from '~/UIKit/Form/ModalSelect/listTypes/ModalSelectCheckboxList';
import { useSelectedMapWithOrder } from './useSelectedMapWithOrder';
import { ITrainer } from '~/Models/DTO/Trainer.models';

export const ModalSelectTrainerPopover = memo(
  ({ open, name, onClose, anchorEl, selected = [], setFieldValue }: IModalProps<string[]>) => {
    const { currentLanguage } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const { selectedMap, onSelect } = useSelectedMapWithOrder({ initialState: selected });
    const { t } = useLanguage();
    const trainerList = useSelector(selectActiveTrainerList);

    const filteredTrainers = trainerList
      .filter(
        (trainer) =>
          selectedMap[trainer.trainerId] ||
          trainer.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .sort((firstTrainer, secondTrainer) => {
        if (firstTrainer.trainerId in selectedMap && secondTrainer.trainerId in selectedMap) {
          return selectedMap[firstTrainer.trainerId] - selectedMap[secondTrainer.trainerId];
        }
        if (firstTrainer.trainerId in selectedMap) {
          return -1;
        }
        if (secondTrainer.trainerId in selectedMap) {
          return 1;
        }
        return firstTrainer.name.localeCompare(secondTrainer.name, currentLanguage, {
          sensitivity: 'base',
        });
      });

    const onActionTaken = () => {
      setFieldValue(name, Object.keys(selectedMap));
      onClose();
    };

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.currentTarget.value);
    };
    const handleSelect = (trainer: ITrainer) => {
      onSelect(trainer.trainerId);
    };

    return (
      <ModalSelectPopover
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        title={t('form.field.trainer')}
        actionName={t('app.checkin.select.done')}
        onActionTaken={onActionTaken}
        search={{
          id: 'searchTrainers',
          value: searchQuery,
          placeholder: t('app.trainer.searchForTrainer'),
          handleSearch,
        }}
      >
        <ModalSelectCheckboxList
          idName="trainerId"
          list={filteredTrainers}
          onSelect={handleSelect}
          selectedMap={selectedMap}
        />
      </ModalSelectPopover>
    );
  },
);
